import React, {FC, useCallback, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {useFormik} from 'formik'
import {IBrandModel, initialBrand, validationBrand} from './BrandModel'
import {createBrand, deleteBrand, loadBrands, updateBrand} from './Api'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {BrandTable} from './BrandTable'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'

const BrandForm: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [brands, setBrands] = useState<IBrandModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const formik = useFormik<IBrandModel>({
    initialValues: initialBrand,
    validationSchema: validationBrand,
    enableReinitialize: true,
    onSubmit: async (values, {resetForm}) => {
      setLoading(true)
      try {
        if (selectedId) {
          await updateBrand(selectedId, values.name)
          showSuccessToast('Marka başarıyla güncellendi')
        } else {
          await createBrand(values.name)
          showSuccessToast('Marka başarıyla oluşturuldu')
        }
        resetForm()
        setSelectedId(null)
        setCurrentPage(0)
        await fetchBrands(0)
      } catch (error) {
        showErrorToast('Bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
  })

  const fetchBrands = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const response = await loadBrands(page)
      setBrands(response.data.payload.content)
      setTotalPages(response.data.payload.totalPages)
    } catch (error) {
      showErrorToast('Markalar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBrands(0)
  }, [fetchBrands])

  const handleEdit = useCallback((brand: IBrandModel) => {
    setSelectedId(brand.id ?? null)
    formik.setValues({id: brand.id, name: brand.name})
  }, [formik])

  const handleDeleteClick = useCallback((brand: IBrandModel) => {
    setSelectedId(brand.id ?? null)
    setShowModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!selectedId) return
    setLoading(true)
    try {
      await deleteBrand(selectedId)
      showSuccessToast('Marka başarıyla silindi')
      await fetchBrands(currentPage)
    } catch (error) {
      showErrorToast('Marka silinirken hata oluştu')
    } finally {
      setShowModal(false)
      setSelectedId(null)
      setLoading(false)
    }
  }, [selectedId, currentPage, fetchBrands])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchBrands(newPage)
      }
    },
    [totalPages, fetchBrands]
  )

  const handleCancel = () => {
    formik.resetForm()
    setSelectedId(null)
  }

  return (
    <>
      {/* Form Card */}
      <div className='card mb-5'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>
              {selectedId ? 'Marka Güncelle' : 'Yeni Marka Ekle'}
            </span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              {selectedId ? 'Mevcut markayı düzenleyin' : 'Yeni bir marka tanımlayın'}
            </span>
          </h3>
        </div>
        <form onSubmit={formik.handleSubmit} className='form'>
          <div className='card-body pt-0'>
            <div className='row'>
              <div className='col-lg-6 fv-row'>
                <label className='form-label required fw-bold fs-6'>Marka Adı</label>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Marka adı giriniz'
                  {...formik.getFieldProps('name')}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.name}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            {selectedId && (
              <button
                type='button'
                className='btn btn-light me-3'
                onClick={handleCancel}
              >
                İptal
              </button>
            )}
            <button type='submit' className='btn btn-primary' disabled={loading}>
              {!loading && (selectedId ? 'Güncelle' : 'Kaydet')}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Lütfen bekleyin...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Table Card */}
      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Markalar</span>
            <span className='text-muted mt-1 fw-bold fs-7'>Marka listesi</span>
          </h3>
        </div>
        <div className='card-body pt-0'>
          <BrandTable
            data={brands}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={loading}
          />
        </div>
      </div>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu markayı silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />
    </>
  )
}

const BrandPage: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={[]}>Marka Tanımı</PageTitle>
      <BrandForm />
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {BrandPage}